import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

interface ConversationCardProps {
  conversation: any;
  bucket: 'REQUIRED' | 'OPTIONAL' | 'NO_NEED';
  formatTimeAgo: (d: any) => string;
  getLatestReason: (c: any) => string;
}

export const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  bucket,
  formatTimeAgo,
  getLatestReason,
}) => {
  const [t] = useTranslation('collab');
  const navigate = useNavigate();
  const { agentId = '' } = useParams();
  const tagName = conversation.current_primary_tag?.tag_name || '—';
  const tagColor = conversation.current_primary_tag?.color;
  const score = conversation.stable_intervention?.score;
  const lastAt =
    conversation.stable_intervention?.last_evaluated_at ||
    conversation.updated_at ||
    conversation.created_at;
  const reason = getLatestReason(conversation);

  const openConversation = () => {
    const convId = conversation?._id;
    const personId = conversation?.person_id;
    if (!agentId || !convId || !personId) return;
    navigate(
      `/agent/${agentId}/conversations/${personId}?conversationId=${convId}`,
    );
  };

  const renderReason = () => {
    if (bucket === 'NO_NEED') {
      if (!reason && (score === undefined || score === 0)) {
        return t('to_be_defined', 'To be defined');
      }
      // Hide score/empty reason for NO_NEED minimal look
      return reason || t('to_be_defined', 'To be defined');
    }
    return reason || t('to_be_defined', 'To be defined');
  };

  return (
    <div
      className="card card-compact bg-base-100 border-base-200 hover:border-primary cursor-pointer border shadow-sm transition-shadow hover:shadow-md"
      onClick={openConversation}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openConversation();
        }
      }}
    >
      <div className="card-body p-3">
        <div className="mb-1 flex items-center justify-between gap-2">
          {/* Bucket badge removed intentionally per feedback */}
          <span className="text-base-content/60 text-xs">
            {formatTimeAgo(lastAt)}
          </span>
        </div>
        <div className="mb-1 flex items-center gap-2">
          <span
            className="badge badge-sm truncate"
            style={{ backgroundColor: tagColor || 'var(--p)', color: '#fff' }}
          >
            {tagName}
          </span>
          {bucket !== 'NO_NEED' && score != null && (
            <span className="text-base-content/60 text-[10px]">
              {Number(score).toFixed(2)}
            </span>
          )}
        </div>
        <p className="text-base-content/80 line-clamp-3 text-xs">
          {renderReason()}
        </p>
      </div>
    </div>
  );
};
